/**
 * @file
 * This file contains utility functions designed to transform input values.
 *
 * These functions are primarily intended for components integrated with Formly, where properties
 * are assigned dynamically and may lack proper type safety. By providing robust defaults,
 * the functions below help prevent runtime issues. They can, however, also be utilized
 * in any other scenario requiring a reliable default.
 *
 * Note: All transform functions within this file must be defined using the classic function syntax.
 */

/**
 * Transforms a value to a boolean with `false` fallback. Intended to be used as a transform function of an input.
 * Unlike Angular's `booleanAttribute`, this function returns `false` for any non-boolean input.
 *
 * @remarks
 * This transform function provides type-safe boolean conversion for Angular Input properties.
 * It's particularly useful in components integrated with Formly where properties are assigned
 * dynamically and may lack proper type safety. By returning `false` for any non-boolean input,
 * it prevents runtime issues that could occur with truthy/falsy values.
 *
 * @param value - The value to convert. If not a boolean, it defaults to `false`.
 * @returns Provided boolean or `false` if the input was not a boolean.
 *
 * @example
 *
 * ```typescript
 * import { withDefaultFalse } from '@backbase/ui-ang/base-classes';
 *
 * @Component({
 *   selector: 'bb-input-text',
 * })
 * export class InputTextComponent extends InputBaseComponent {
 *   @Input({ transform: withDefaultFalse }) showCharCounter = false;
 * }
 * ```
 */
export function withDefaultFalse(value?: unknown): boolean {
  return typeof value === 'boolean' ? value : false;
}

/**
 * Transforms a value to a boolean with `true` fallback. Intended to be used as a transform function of an input.
 * Unlike Angular's `booleanAttribute`, this function returns `true` for any non-boolean input.
 *
 * @remarks
 * This transform function provides type-safe boolean conversion for Angular Input properties
 * that should default to `true`. It's particularly useful for enabling features by default
 * while allowing explicit disabling. Returns `true` for any non-boolean input, making it
 * suitable for components integrated with Formly or other dynamic property assignment systems.
 *
 * @param value - The value to convert. If not a boolean, it defaults to `true`.
 * @returns Provided boolean or `true` if the input was not a boolean.
 *
 * @example
 *
 * ```typescript
 * import { withDefaultTrue } from '@backbase/ui-ang/base-classes';
 *
 * @Component({
 *   selector: 'bb-input-text',
 * })
 * export class InputTextComponent extends InputBaseComponent {
 *   @Input({ transform: withDefaultTrue }) maskValidator = true;
 * }
 * ```
 */
export function withDefaultTrue(value?: unknown): boolean {
  return typeof value === 'boolean' ? value : true;
}

/**
 * Transforms a value to a string. Intended to be used as a transform function of an input.
 * Can be wrapped into another function to provide different default value.
 *
 * @remarks
 * This transform function provides type-safe string conversion for Angular Input properties.
 * It automatically trims whitespace from string values and falls back to the provided
 * fallback value for empty or non-string inputs. The function is commonly used directly
 * for simple string inputs or wrapped in other transform functions to provide custom defaults.
 *
 * @param value - The value to convert. If not a string, the `fallBackValue` is used.
 * @param fallbackValue - The string to return if `value` is not a string. Defaults to an empty string (`''`).
 * @returns Provided string or `fallBackValue` if the input was not a string.
 *
 * @example
 *
 * ```typescript
 * import { withDefaultString } from '@backbase/ui-ang/base-classes';
 *
 * @Component({
 *   selector: 'bb-textarea',
 * })
 * export class TextareaComponent extends InputBaseComponent {
 *   @Input({ transform: withDefaultString }) placeholder = '';
 * }
 * ```
 */
export function withDefaultString(value?: unknown, fallbackValue: string = ''): string {
  if (typeof value === 'string') {
    return value.trim() || fallbackValue;
  }

  return fallbackValue;
}
